import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const KEY = "tessa.favorites.v1";
const FavoritesContext = createContext(null);

// localStorage-backed favorites. Stores ONLY minimal reference fields —
// never names, emails, phones, messages, or order details.
export function FavoritesProvider({ children }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(KEY);
      if (raw) setItems(JSON.parse(raw));
    } catch (_) { /* ignore */ }
  }, []);

  const persist = useCallback((next) => {
    setItems(next);
    try { window.localStorage.setItem(KEY, JSON.stringify(next)); } catch (_) {}
  }, []);

  const add = useCallback((item) => {
    setItems((prev) => {
      if (prev.some((p) => p.id === item.id)) return prev;
      const next = [...prev, { id: item.id, name: item.name, image: item.image, category_label: item.category_label }];
      try { window.localStorage.setItem(KEY, JSON.stringify(next)); } catch (_) {}
      return next;
    });
  }, []);

  const remove = useCallback((id) => {
    setItems((prev) => {
      const next = prev.filter((p) => p.id !== id);
      try { window.localStorage.setItem(KEY, JSON.stringify(next)); } catch (_) {}
      return next;
    });
  }, []);

  const toggle = useCallback((item) => {
    setItems((prev) => {
      const exists = prev.some((p) => p.id === item.id);
      const next = exists ? prev.filter((p) => p.id !== item.id)
                          : [...prev, { id: item.id, name: item.name, image: item.image, category_label: item.category_label }];
      try { window.localStorage.setItem(KEY, JSON.stringify(next)); } catch (_) {}
      return next;
    });
  }, []);

  const isFavorite = useCallback((id) => items.some((p) => p.id === id), [items]);

  const clear = useCallback(() => persist([]), [persist]);

  return (
    <FavoritesContext.Provider value={{ items, add, remove, toggle, isFavorite, clear }}>
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites() {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error("useFavorites must be used inside FavoritesProvider");
  return ctx;
}
